import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../../components/Seo';
import { seo } from '../../seo/seoData';
import { useTranslation } from 'react-i18next';
import { Users, ArrowLeft, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalDraftBanner from '../../components/LegalDraftBanner';

// =============================================================================
// LEGAL DRAFT — REQUIRES AVOCAT REVIEW BEFORE PUBLIC LAUNCH
// =============================================================================
// The Chauffeur Code of Conduct ("Règlement Chauffeurs") was scaffolded by an
// earlier session. It MUST be reviewed and finalized by an avocat (specialised
// in droit social and droit des plateformes) before public launch.
//
// HIGH URSSAF / requalification risk areas — every clause below must be
// validated against post-Uber Cassation sociale jurisprudence (notably Soc. 4
// mars 2020 n° 19-13.316, plus the 2022 EU Platform Work Directive trajectory):
//
//   - SANCTION SYSTEM (Article 6, Levels 1–4): looks like an employer's
//     disciplinary power (pouvoir disciplinaire). For independent
//     contractors the platform should reserve the right to deactivate access
//     for breach of contract, not impose escalating "punishments". The four
//     graduated levels mirror the staircase of an employee disciplinary
//     procedure (Art L. 1331-1 Code du travail).
//   - "Score 4.5/5 minimum" requirement (Article 7) reads as performance
//     management of a salaried worker. Cassation has held that quantitative
//     scoring + automatic suspension is a strong indicator of subordination.
//   - "Tenue vestimentaire imposée" / "Vestimentaire soignée" (Article 2):
//     if interpreted as an imposed uniform or dress code, this is a classic
//     subordination indicator. Wording must remain a quality recommendation,
//     not an imposed standard.
//   - Article 3.2 cancellation penalty staircase (Avertissement → Suspension
//     24h → 7 jours → exclusion) mirrors the Code du travail disciplinary
//     ladder and is the strongest single requalification signal in the file.
//   - Article 3.3 "5 minutes avant l'heure convenue": instruction-style
//     wording that may suggest the platform dictates work conditions.
//   - Article 4 obligations include items the chauffeur owes to the State
//     (Code de la route, taxes) — these are fine; but obligations owed to
//     the platform must be framed as contractual minimums, not workplace
//     rules.
//
// Avocat must rewrite this document into a "Charte de qualité" tone (charter
// of quality voluntarily adhered to) rather than a "Règlement" (workplace
// rules document) to materially reduce requalification exposure.
//
// FR is authoritative; non-FR versions are courtesy translations marked via
// LegalDraftBanner.
// =============================================================================

const ChauffeurReglementPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="chauffeur-reglement-page">
          <Seo {...seo.chauffeurReglement} />
      <LegalDraftBanner />
      <section className="py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back link */}
            <Link to="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" />
              {t('legalPages.common.backToHome')}
            </Link>

            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
                  {t('legalPages.chauffeurReglement.title')}
                </h1>
                <p className="text-neutral-500 text-sm mt-1">{t('legalPages.common.lastUpdated')}</p>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-6 mb-10">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-semibold text-yellow-400 mb-2">{t('legalPages.chauffeurReglement.important.title')}</h3>
                  <p className="text-neutral-400 text-sm">{t('legalPages.chauffeurReglement.important.body')}</p>
                </div>
              </div>
            </div>

            <div className="prose prose-invert max-w-none space-y-10">
              {/* Article 1 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art1.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art1.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5, 6].map(i => <li key={i}>{t(`legalPages.chauffeurReglement.art1.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 2 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art2.title')}</h2>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="bg-green-500/5 border border-green-500/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <h3 className="font-medium text-green-400">{t('legalPages.chauffeurReglement.art2.expectedTitle')}</h3>
                    </div>
                    <ul className="text-neutral-400 text-sm space-y-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <li key={i}>{t(`legalPages.chauffeurReglement.art2.expectedList${i}`)}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <h3 className="font-medium text-red-400">{t('legalPages.chauffeurReglement.art2.forbiddenTitle')}</h3>
                    </div>
                    <ul className="text-neutral-400 text-sm space-y-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <li key={i}>{t(`legalPages.chauffeurReglement.art2.forbiddenList${i}`)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              {/* Article 3 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art3.title')}</h2>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurReglement.art3.sub31Title')}</h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art3.sub31Body')}</p>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurReglement.art3.sub32Title')}</h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art3.sub32Body')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4].map(i => <li key={i}>{t(`legalPages.chauffeurReglement.art3.sub32List${i}`)}</li>)}
                </ul>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t('legalPages.chauffeurReglement.art3.sub33Title')}</h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art3.sub33Body')}</p>
              </section>

              {/* Article 4 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art4.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art4.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5, 6].map(i => <li key={i}>{t(`legalPages.chauffeurReglement.art4.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 5 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art5.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art5.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5].map(i => <li key={i}>{t(`legalPages.chauffeurReglement.art5.list${i}`)}</li>)}
                </ul>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.chauffeurReglement.art5.outro')}</p>
              </section>

              {/* Article 6 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art6.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art6.intro')}</p>

                <div className="grid gap-4 mt-6">
                  <div className="flex items-center gap-4 bg-yellow-500/5 border border-yellow-500/10 rounded-xl p-4">
                    <span className="text-yellow-400 font-heading font-bold">{t('legalPages.chauffeurReglement.art6.level1Label')}</span>
                    <span className="text-neutral-400">{t('legalPages.chauffeurReglement.art6.level1Desc')}</span>
                  </div>
                  <div className="flex items-center gap-4 bg-orange-500/5 border border-orange-500/10 rounded-xl p-4">
                    <span className="text-orange-400 font-heading font-bold">{t('legalPages.chauffeurReglement.art6.level2Label')}</span>
                    <span className="text-neutral-400">{t('legalPages.chauffeurReglement.art6.level2Desc')}</span>
                  </div>
                  <div className="flex items-center gap-4 bg-red-500/5 border border-red-500/10 rounded-xl p-4">
                    <span className="text-red-400 font-heading font-bold">{t('legalPages.chauffeurReglement.art6.level3Label')}</span>
                    <span className="text-neutral-400">{t('legalPages.chauffeurReglement.art6.level3Desc')}</span>
                  </div>
                  <div className="flex items-center gap-4 bg-red-600/5 border border-red-600/10 rounded-xl p-4">
                    <span className="text-red-500 font-heading font-bold">{t('legalPages.chauffeurReglement.art6.level4Label')}</span>
                    <span className="text-neutral-400">{t('legalPages.chauffeurReglement.art6.level4Desc')}</span>
                  </div>
                </div>

                <p className="text-neutral-400 leading-relaxed mt-6">{t('legalPages.chauffeurReglement.art6.outro')}</p>
              </section>

              {/* Article 7 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art7.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art7.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3].map(i => <li key={i}>{t(`legalPages.chauffeurReglement.art7.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 8 - Contact */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.chauffeurReglement.art8.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.chauffeurReglement.art8.intro')}</p>
                <div className="mt-4 text-neutral-400 space-y-1">
                  <p>Email : chauffeurs@blackmatte.fr</p>
                  <p>Téléphone : 09 39 24 23 20</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ChauffeurReglementPage;
